import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Separator } from '@/components/ui-kit/separator';
import {
  ArrowRight,
  Award,
  CheckCircle2,
  Clock,
  FileText,
  LayoutDashboard,
  Lock,
  Mail,
  Mountain,
  RefreshCcw,
  Scale,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { LegalDocument } from '@/schemas/schema';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { loadTemplateReview } from '../components/review/TemplateReviewStorage';
import { useGetOrgContractDocumentById } from '@/graphql';
import { useGetContractDocumentEvents } from '@/modules/org/contracts/hooks/use-contract-document-events';
import { useMyOgnization } from '@/api-client/use-my-ognization';
import { useGetOrganizationsByBlocksOrgIds } from '@/graphql/queries/organizations';
import { GUEST_ORG_SENTINEL } from '@/modules/org/contracts/types/contract-document.types';
import { ForbiddenCard } from '@/components/common/ForbiddenCard';
import Loader from '@/components/common/Loader';
import DocumentStatusBadge from '@/modules/org/contracts/components/document-status-badge';
import { ContractEventsTimeline } from '@/modules/org/contracts/components/contract-events-timeline';
import type { DocumentStatus } from '@/modules/org/contracts/constants/mock-contract-documents';

interface SignatureSummary {
  documentId?: string;
  trackingId?: string;
  fileId?: string;
}

interface PayloadSummary {
  document?: LegalDocument;
  formData?: Record<string, unknown> | null;
  signatories?: Array<{
    name: string;
    email: string;
    role?: string;
    order?: number;
    party?: string;
  }>;
  templateSlug?: string;
  templateTitle?: string;
}

export default function SignatureSuccessPage() {
  const params = useParams();
  const router = useNavigate();
  const slug = params.slug as string;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Document id passed from the review page after successful rollout. Without
  // it we still render (legacy / sessionStorage-only flow), but the UDS
  // status + event data will be empty.
  const documentId = searchParams.get('document') ?? '';

  const {
    data: contractDoc,
    isLoading: isDocumentLoading,
    isFetching: isDocumentFetching,
    refetch: refetchDocument,
  } = useGetOrgContractDocumentById(documentId, !!documentId);

  const { data: myOrgs, isLoading: isIamOrgsLoading } = useMyOgnization();
  const blocksOrgIds = useMemo(
    () => myOrgs?.organizations?.map((org) => org.itemId) ?? [],
    [myOrgs]
  );
  const { data: udsOrgs, isLoading: isUdsOrgsLoading } = useGetOrganizationsByBlocksOrgIds(
    blocksOrgIds,
    { requireSignatureLink: false, enabled: blocksOrgIds.length > 0 }
  );

  // This page shows the contract's status, signatory emails and full event
  // log, so an org-owned document is readable only by that org's members.
  const owner = contractDoc?.OrganizationId;
  const access = useMemo(() => {
    if (!contractDoc || !owner || owner === GUEST_ORG_SENTINEL) return 'allowed' as const;
    if (isIamOrgsLoading || (blocksOrgIds.length > 0 && isUdsOrgsLoading)) {
      return 'checking' as const;
    }
    const udsOrgIds = new Set((udsOrgs ?? []).map((org) => org.ItemId));
    return udsOrgIds.has(owner) ? ('allowed' as const) : ('forbidden' as const);
  }, [blocksOrgIds.length, contractDoc, isIamOrgsLoading, isUdsOrgsLoading, owner, udsOrgs]);

  const {
    data: eventsResult,
    isLoading: isEventsLoading,
    isFetching: isEventsFetching,
    refetch: refetchEvents,
  } = useGetContractDocumentEvents(documentId, !!documentId && access === 'allowed');

  const isRefreshing = isDocumentFetching || isEventsFetching;
  const handleRefresh = () => {
    void refetchDocument();
    void refetchEvents();
  };

  const documentStatus = (contractDoc?.Status as DocumentStatus | undefined) ?? undefined;

  // Map signatory email → latest signatory_signed_* event for that person.
  const signatoryEventByEmail = useMemo(() => {
    const map = new Map<string, { eventType: string; occurredAt: string; signedTime?: string }>();
    (eventsResult?.items ?? []).forEach((e) => {
      if (
        (e.EventType !== 'signatory_signed_success' && e.EventType !== 'signatory_signed_failed') ||
        !e.SignatoryEmail
      ) {
        return;
      }
      const existing = map.get(e.SignatoryEmail);
      const occurredAt = e.OccurredAt ?? e.CreatedDate ?? '';
      if (!existing || occurredAt > existing.occurredAt) {
        map.set(e.SignatoryEmail, {
          eventType: e.EventType,
          occurredAt,
          signedTime: e.SignedTime,
        });
      }
    });
    return map;
  }, [eventsResult?.items]);

  // All workflow events for the page-level timeline — newest first.
  const workflowEvents = useMemo(() => {
    return (eventsResult?.items ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.OccurredAt ?? b.CreatedDate ?? '').getTime() -
          new Date(a.OccurredAt ?? a.CreatedDate ?? '').getTime()
      );
  }, [eventsResult?.items]);

  //
  // 1. Retrieve Data from Session Storage
  const payload = useMemo<PayloadSummary | null>(() => {
    if (typeof window === 'undefined') return null;

    // Try to get from template review storage first
    const reviewData = loadTemplateReview(slug);
    if (reviewData) {
      return {
        document: reviewData.document,
        formData: reviewData.formData,
        signatories: reviewData.document?.signatories?.map((sig) => ({
          name: sig.name,
          email: sig.email || '',
          role: sig.title || sig.party,
          party: sig.party,
        })),
        templateSlug: reviewData.templateSlug,
        templateTitle: reviewData.templateTitle,
      };
    }

    // Fallback to legacy storage
    const raw = window.sessionStorage.getItem('signature-last-payload');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PayloadSummary;
    } catch (error) {
      console.warn('Unable to parse signature payload cache', error);
      return null;
    }
  }, [slug]);

  const summary = useMemo<SignatureSummary | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem('signature-last-result');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SignatureSummary;
    } catch (error) {
      console.warn('Unable to parse signature result cache', error);
      return null;
    }
  }, []);

  // Redirect if no payload found. Skipped while access is still being decided
  // or already denied — otherwise a non-member is bounced to the template page
  // before the 403 can render.
  useEffect(() => {
    if (access !== 'allowed') return;
    if (!payload && typeof window !== 'undefined') {
      // Redirect to template page if no data found
      router(`/templates/${slug}`);
    }
  }, [access, payload, router, slug]);

  // Get document title and first signatory name
  const documentTitle =
    payload?.templateTitle ||
    payload?.document?.metadata?.title ||
    t('EMPLOYMENTAGREEMENT_SUCCESS_DOCUMENTFALLBACK');

  const firstSignatoryName: string =
    payload?.signatories?.[0]?.name ||
    payload?.document?.signatories?.[0]?.name ||
    t('EMPLOYMENTAGREEMENT_SUCCESS_SIGNATORYFALLBACK');

  const signatories = payload?.signatories
    ? [...payload.signatories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : payload?.document?.signatories?.map((sig) => ({
        name: sig.name,
        email: sig.email || '',
        role: sig.title || sig.party,
        order: 0,
      })) || [];

  // Hold the page until the contract row and the membership verdict are both
  // in, so a non-member never sees a frame of it. Mirrors the review route.
  if ((documentId && isDocumentLoading) || access === 'checking') {
    return <Loader />;
  }

  if (access === 'forbidden') {
    return (
      <ForbiddenCard
        description={t('FORBIDDEN_CONTRACT_DESCRIPTION', {
          defaultValue:
            "This document belongs to an organization you're not a member of. Ask an admin of that organization to add you, or switch to an account that has access.",
        })}
        onBack={() => router(-1)}
      />
    );
  }

  if (!payload) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bg))] flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{t('EMPLOYMENTAGREEMENT_SUCCESS_LOADING')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--fg))] flex flex-col font-body">
      {/* Top Navigation / Branding */}
      <header className="w-full border-b border-border bg-[hsl(var(--bg))] py-3 sm:py-4">
        <div className="container mx-auto px-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 shrink-0 bg-selise-blue-500 rounded-lg flex items-center justify-center text-white">
              <FileText size={18} />
            </div>
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight text-selise-blue-500 truncate">
              {t('EMPLOYMENTAGREEMENT_SUCCESS_BRANDTITLE')}
            </span>
          </div>
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-selise-blue text-xs sm:text-sm shrink-0 px-2 sm:px-4"
          >
            <Link to={`/templates/${slug}/generate`}>
              {t('EMPLOYMENTAGREEMENT_SUCCESS_CREATEANOTHER')}
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-6 sm:py-10 lg:py-12 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12">
          {/* Left Column: Success State & Document Info */}
          <div className="lg:col-span-7 space-y-6 sm:space-y-8">
            {/* Success Badge & Headline */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/20 text-[hsl(var(--success))] text-sm font-medium">
                  <CheckCircle2 size={16} />
                  <span>{t('EMPLOYMENTAGREEMENT_SUCCESS_BADGE')}</span>
                </div>
                {documentStatus ? <DocumentStatusBadge status={documentStatus} /> : null}
              </div>

              <h1 className="font-heading text-xl sm:text-3xl font-bold text-[hsl(var(--fg))] leading-tight">
                {t('EMPLOYMENTAGREEMENT_SUCCESS_HEADLINE', {
                  documentTitle,
                  employeeName: firstSignatoryName,
                })}
              </h1>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                {t('EMPLOYMENTAGREEMENT_SUCCESS_LEAD')}
              </p>
            </div>

            {/* Document Timeline / Signatories */}
            <Card className="border-border shadow-sm overflow-hidden">
              <CardHeader className="bg-[hsl(var(--bg))] border-b border-border py-4">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="font-subheading text-base flex items-center gap-2">
                    <Clock className="text-selise-blue" size={16} />
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_SIGNINGSTATUSTITLE')}
                  </CardTitle>
                  {documentId ? (
                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      aria-label={t('ORGCONTRACTS_TIMELINE_REFRESH')}
                      title={t('ORGCONTRACTS_TIMELINE_REFRESH')}
                      className="inline-flex size-8 items-center justify-center rounded-md text-medium-emphasis transition hover:bg-muted hover:text-high-emphasis disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCcw className={cn('size-4', isRefreshing && 'animate-spin')} />
                    </button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {signatories.map((signatory, idx) => {
                    const event = signatory.email
                      ? signatoryEventByEmail.get(signatory.email)
                      : undefined;
                    const isSigned = event?.eventType === 'signatory_signed_success';
                    const isFailed = event?.eventType === 'signatory_signed_failed';
                    const eventTime = event?.signedTime ?? event?.occurredAt;
                    return (
                      <div
                        key={idx}
                        className="flex items-start gap-3 sm:items-center sm:gap-4 p-3 sm:p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-full bg-selise-blue/10 flex items-center justify-center text-selise-blue font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-sm truncate">{signatory.name}</p>
                            {/* Status badge sits next to the name on phones so
                                it doesn't get pushed off-screen by a long name
                                + a long "Waiting for signature" label. */}
                            {isSigned ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 shrink-0">
                                <CheckCircle2 className="size-3 mr-1" />
                                {t('EMPLOYMENTAGREEMENT_SUCCESS_SIGNINGSTATUSSIGNED', {
                                  defaultValue: 'Signed',
                                })}
                              </Badge>
                            ) : isFailed ? (
                              <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100 shrink-0">
                                <XCircle className="size-3 mr-1" />
                                {t('EMPLOYMENTAGREEMENT_SUCCESS_SIGNINGSTATUSFAILED', {
                                  defaultValue: 'Failed',
                                })}
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="bg-[hsl(var(--globe-grey))]/10 text-globe-grey shrink-0"
                              >
                                {t('EMPLOYMENTAGREEMENT_SUCCESS_SIGNINGSTATUSWAITING')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {signatory.email}
                          </p>
                          {signatory.role && (
                            <p className="text-xs text-muted-foreground truncate">
                              {signatory.role}
                            </p>
                          )}
                          {eventTime ? (
                            <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                              {new Date(eventTime).toLocaleString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                  {signatories.length === 0 && (
                    <div className="p-6 text-center text-muted-foreground text-sm">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_NOSIGNATORIES')}
                    </div>
                  )}
                </div>
                <div className="bg-muted/30 p-3 sm:p-4 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2 border-t border-border">
                  <span className="min-w-0 break-all">
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_TRACKINGIDLABEL')}{' '}
                    <span className="font-mono">{summary?.trackingId || '---'}</span>
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <ShieldCheck size={12} />
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_AUDITLOGACTIVE')}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow events — uses the same rich timeline design as the */}
            {/* contract row's Timeline dialog. */}
            {documentId ? (
              <Card className="border-border shadow-sm overflow-hidden">
                <CardHeader className="bg-[hsl(var(--bg))] border-b border-border py-4">
                  <CardTitle className="font-subheading text-base flex items-center gap-2">
                    <Clock className="text-selise-blue" size={16} />
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_WORKFLOWEVENTSTITLE', {
                      defaultValue: 'Workflow events',
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ContractEventsTimeline
                    events={workflowEvents}
                    isLoading={isDocumentLoading || isEventsLoading}
                  />
                </CardContent>
              </Card>
            ) : null}

            {/* Next Steps */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="font-subheading font-semibold text-base sm:text-lg">
                {t('EMPLOYMENTAGREEMENT_SUCCESS_NEXTSTEPSTITLE')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex gap-3 p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <Mail className="text-selise-blue shrink-0" size={20} />
                  <div className="text-sm min-w-0">
                    <span className="font-semibold block mb-1">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_NEXTSTEPSEMAILTITLE')}
                    </span>
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_NEXTSTEPSEMAILDESCRIPTION')}
                  </div>
                </div>
                <div className="flex gap-3 p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <FileText className="text-selise-blue shrink-0" size={20} />
                  <div className="text-sm min-w-0">
                    <span className="font-semibold block mb-1">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_NEXTSTEPSFINALCOPYTITLE')}
                    </span>
                    {t('EMPLOYMENTAGREEMENT_SUCCESS_NEXTSTEPSFINALCOPYDESCRIPTION')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Signup Conversion Card */}
          <div className="lg:col-span-5">
            {/* Sticky only on desktop — on mobile the card is stacked below
                the left column so sticky positioning would do nothing useful. */}
            <div className="lg:sticky lg:top-8">
              <div className="relative">
                <Card className="relative border-selise-blue/30 shadow-xl overflow-hidden bg-white dark:bg-card">
                  {/* Header with Light Gradient */}
                  <div className="bg-linear-to-br from-[hsl(var(--selise-blue))]/10 to-[hsl(var(--sky-blue))]/10 p-6 sm:p-8 text-center border-b border-selise-blue/10">
                    <div className="mx-auto h-12 w-12 bg-selise-blue-500 rounded-xl flex items-center justify-center mb-3 sm:mb-4 shadow-lg shadow-[hsl(var(--selise-blue))]/20">
                      <Sparkles className="text-white h-6 w-6" />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold mb-2 text-selise-blue">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_CONVERSIONTITLE')}
                    </h2>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_CONVERSIONSUBTITLE')}
                    </p>
                  </div>

                  <CardContent className="p-5 sm:p-8 space-y-5 sm:space-y-6">
                    {/* Value Props List */}
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <div className="p-1.5 rounded-lg bg-selise-blue/10 mt-0.5">
                          <LayoutDashboard className="h-4 w-4 text-selise-blue" />
                        </div>
                        <div>
                          <span className="font-semibold text-sm block text-[hsl(var(--fg))]">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPTRACKINGTITLE')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPTRACKINGDESCRIPTION', {
                              employeeName: firstSignatoryName,
                            })}
                          </span>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="p-1.5 rounded-lg bg-selise-blue/10 mt-0.5">
                          <Clock className="h-4 w-4 text-selise-blue" />
                        </div>
                        <div>
                          <span className="font-semibold text-sm block text-[hsl(var(--fg))]">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPREMINDERSTITLE')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPREMINDERSDESCRIPTION')}
                          </span>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="p-1.5 rounded-lg bg-selise-blue/10 mt-0.5">
                          <Scale className="h-4 w-4 text-selise-blue" />
                        </div>
                        <div>
                          <span className="font-semibold text-sm block text-[hsl(var(--fg))]">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPCOMPLIANCETITLE')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t('EMPLOYMENTAGREEMENT_SUCCESS_VALUEPROPCOMPLIANCEDESCRIPTION')}
                          </span>
                        </div>
                      </li>
                    </ul>

                    <Separator className="bg-border" />

                    {/* CTA Buttons */}
                    <div className="space-y-3">
                      <Button className="w-full" variant={'primary'}>
                        <Link
                          to="https://selise.app/signup"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center"
                        >
                          {t('EMPLOYMENTAGREEMENT_SUCCESS_CTA')}{' '}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                      <p className="text-center text-xs text-muted-foreground">
                        {t('EMPLOYMENTAGREEMENT_SUCCESS_CTANOTE')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
                <p className="text-xs text-center font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('EMPLOYMENTAGREEMENT_SUCCESS_TRUSTTITLE')}
                </p>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Mountain className="h-5 w-5 text-globe-grey" />
                    <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_TRUSTSWISSHOSTED')}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Award className="h-5 w-5 text-globe-grey" />
                    <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_TRUSTEIDAS')}
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Lock className="h-5 w-5 text-globe-grey" />
                    <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">
                      {t('EMPLOYMENTAGREEMENT_SUCCESS_TRUSTISO')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
